import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PaymentIntegration = ({ bookingData, hostel, onUpdate, onNext, onPrev }) => {
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    mobileNumber: '',
    mobileProvider: 'mtn'
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState({});

  // Calculate total cost
  const roomType = bookingData.roomSelection?.roomType;
  const duration = bookingData.bookingDetails?.duration;

  const calculateTotal = () => {
    if (!roomType) return 0;

    let basePrice = roomType.price;
    let multiplier = 1;

    switch (duration) {
      case 'semester':
        multiplier = 4.5; // Average semester length
        break;
      case 'academic_year':
        multiplier = 10; // Full academic year
        break;
      default:
        multiplier = 1;
    }

    const subtotal = basePrice * multiplier;
    const serviceFee = subtotal * 0.05; // 5% service fee
    const total = subtotal + serviceFee;

    return {
      subtotal: Math.round(subtotal),
      serviceFee: Math.round(serviceFee),
      total: Math.round(total)
    };
  };

  const costs = calculateTotal();

  const handleInputChange = (field, value) => {
    setPaymentData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validatePaymentData = () => {
    const newErrors = {};

    if (paymentMethod === 'card') {
      if (!paymentData.cardNumber.replace(/\s/g, '').match(/^\d{16}$/)) {
        newErrors.cardNumber = 'Please enter a valid 16-digit card number';
      }
      if (!paymentData.expiryDate.match(/^(0[1-9]|1[0-2])\/\d{2}$/)) {
        newErrors.expiryDate = 'Please enter expiry date in MM/YY format';
      }
      if (!paymentData.cvv.match(/^\d{3,4}$/)) {
        newErrors.cvv = 'Please enter a valid CVV';
      }
      if (!paymentData.cardholderName.trim()) {
        newErrors.cardholderName = 'Cardholder name is required';
      }
    } else if (paymentMethod === 'mobile') {
      if (!paymentData.mobileNumber.match(/^(\+256|0)[17]\d{8}$/)) {
        newErrors.mobileNumber = 'Please enter a valid Ugandan mobile number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePayment = async () => {
    if (!validatePaymentData()) return;

    setIsProcessing(true);

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Mock payment success
      const paymentResult = {
        transactionId: `TXN${Date.now()}`,
        amount: costs.total,
        currency: 'UGX',
        method: paymentMethod,
        status: 'completed',
        timestamp: new Date().toISOString()
      };

      onUpdate(paymentResult);
      onNext();
    } catch (error) {
      setErrors({
        general: 'Payment failed. Please try again or contact support.'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const formatExpiryDate = (value) => {
    const v = value.replace(/\D/g, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const paymentMethods = [
    {
      id: 'card',
      name: 'Credit/Debit Card',
      description: 'Visa, Mastercard, or local cards',
      icon: 'CreditCard',
      fee: 'No extra fees'
    },
    {
      id: 'mobile',
      name: 'Mobile Money',
      description: 'MTN Mobile Money, Airtel Money',
      icon: 'Smartphone',
      fee: 'UGX 1,000 processing fee'
    },
    {
      id: 'bank',
      name: 'Bank Transfer',
      description: 'Direct bank transfer (2-3 business days)',
      icon: 'Building',
      fee: 'UGX 5,000 processing fee'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Payment Information</h2>
        <p className="text-muted-foreground">
          Complete your booking by selecting a payment method and providing payment details.
        </p>
      </div>

      {/* Booking Summary */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h3 className="font-medium text-foreground mb-3">Booking Summary</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Hostel:</span>
            <span className="font-medium">{hostel.name}</span>
          </div>
          <div className="flex justify-between">
            <span>Room Type:</span>
            <span>{bookingData.roomSelection?.roomType?.type}</span>
          </div>
          <div className="flex justify-between">
            <span>Duration:</span>
            <span className="capitalize">{duration?.replace('_', ' ')}</span>
          </div>
          <div className="flex justify-between">
            <span>Check-in Date:</span>
            <span>{new Date(bookingData.bookingDetails?.checkInDate).toLocaleDateString()}</span>
          </div>
          <div className="border-t border-border pt-2 mt-3">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>UGX {costs.subtotal.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span>Service Fee (5%):</span>
              <span>UGX {costs.serviceFee.toLocaleString()}</span>
            </div>
            <div className="flex justify-between font-medium text-lg pt-2 border-t border-border">
              <span>Total:</span>
              <span className="text-primary">UGX {costs.total.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method Selection */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Select Payment Method</h3>
        <div className="grid grid-cols-1 gap-3">
          {paymentMethods.map((method) => (
            <div
              key={method.id}
              className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                paymentMethod === method.id
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => setPaymentMethod(method.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Icon name={method.icon} size={24} className="text-primary" />
                  <div>
                    <h4 className="font-medium text-foreground">{method.name}</h4>
                    <p className="text-sm text-muted-foreground">{method.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">{method.fee}</div>
                  {paymentMethod === method.id && (
                    <Icon name="CheckCircle" size={20} className="text-primary mt-1" />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Form */}
      <div className="space-y-4">
        {paymentMethod === 'card' && (
          <>
            <h3 className="text-lg font-medium">Card Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Card Number *
                </label>
                <input
                  type="text"
                  value={paymentData.cardNumber}
                  onChange={(e) => handleInputChange('cardNumber', formatCardNumber(e.target.value))}
                  placeholder="1234 5678 9012 3456"
                  maxLength="19"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.cardNumber ? 'border-red-500' : 'border-border'
                  }`}
                />
                {errors.cardNumber && (
                  <p className="text-red-500 text-xs mt-1">{errors.cardNumber}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Expiry Date *
                </label>
                <input
                  type="text"
                  value={paymentData.expiryDate}
                  onChange={(e) => handleInputChange('expiryDate', formatExpiryDate(e.target.value))}
                  placeholder="MM/YY"
                  maxLength="5"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.expiryDate ? 'border-red-500' : 'border-border'
                  }`}
                />
                {errors.expiryDate && (
                  <p className="text-red-500 text-xs mt-1">{errors.expiryDate}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  CVV *
                </label>
                <input
                  type="text"
                  value={paymentData.cvv}
                  onChange={(e) => handleInputChange('cvv', e.target.value.replace(/\D/g, '').substring(0, 4))}
                  placeholder="123"
                  maxLength="4"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.cvv ? 'border-red-500' : 'border-border'
                  }`}
                />
                {errors.cvv && (
                  <p className="text-red-500 text-xs mt-1">{errors.cvv}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Cardholder Name *
                </label>
                <input
                  type="text"
                  value={paymentData.cardholderName}
                  onChange={(e) => handleInputChange('cardholderName', e.target.value)}
                  placeholder="John Doe"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.cardholderName ? 'border-red-500' : 'border-border'
                  }`}
                />
                {errors.cardholderName && (
                  <p className="text-red-500 text-xs mt-1">{errors.cardholderName}</p>
                )}
              </div>
            </div>
          </>
        )}

        {paymentMethod === 'mobile' && (
          <>
            <h3 className="text-lg font-medium">Mobile Money Payment</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Mobile Money Provider *
                </label>
                <select
                  value={paymentData.mobileProvider}
                  onChange={(e) => handleInputChange('mobileProvider', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="mtn">MTN Mobile Money</option>
                  <option value="airtel">Airtel Money</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  value={paymentData.mobileNumber}
                  onChange={(e) => handleInputChange('mobileNumber', e.target.value)}
                  placeholder="+256 700 000 000"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.mobileNumber ? 'border-red-500' : 'border-border'
                  }`}
                />
                {errors.mobileNumber && (
                  <p className="text-red-500 text-xs mt-1">{errors.mobileNumber}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  You'll receive a payment prompt on this number
                </p>
              </div>
            </div>
          </>
        )}

        {paymentMethod === 'bank' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <Icon name="Info" size={20} className="text-blue-500 mr-3 mt-0.5" />
              <div>
                <h4 className="text-blue-800 font-medium">Bank Transfer Instructions</h4>
                <div className="text-blue-700 text-sm mt-2 space-y-1">
                  <p><strong>Bank:</strong> Centenary Bank</p>
                  <p><strong>Account Name:</strong> Makerere University Hostels</p>
                  <p><strong>Account Number:</strong> 1234567890</p>
                  <p><strong>Reference:</strong> Your Student ID + Hostel Name</p>
                </div>
                <p className="text-blue-700 text-sm mt-2">
                  Processing takes 2-3 business days. Include your booking reference in the transfer.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <Icon name="AlertCircle" size={20} className="text-red-500 mr-2" />
            <span className="text-red-700 text-sm">{errors.general}</span>
          </div>
        </div>
      )}

      {/* Terms and Security */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start">
          <Icon name="Shield" size={20} className="text-green-500 mr-3 mt-0.5" />
          <div>
            <h4 className="text-green-800 font-medium">Secure Payment</h4>
            <p className="text-green-700 text-sm mt-1">
              Your payment information is encrypted and secure. We use industry-standard security measures to protect your data.
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-6">
        <Button type="button" variant="outline" onClick={onPrev}>
          <Icon name="ArrowLeft" size={16} className="mr-2" />
          Previous
        </Button>
        <Button
          onClick={handlePayment}
          disabled={isProcessing}
          className="min-w-[150px]"
        >
          {isProcessing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Processing...
            </>
          ) : (
            <>
              <Icon name="CreditCard" size={16} className="mr-2" />
              Pay UGX {costs.total.toLocaleString()}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default PaymentIntegration;